<template>
  <section class="py-24">
    <MicsContainer>
      <header class="mb-12" id="all-buildings">
        <h2 class="leading-tight text-4xl font-extrabold">{{$t('buildings_in')}} {{$t('countries.' + $helpers.getConfigurationKey('countryCode'))}}</h2>
      </header>
      <div class="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div v-if="locations.length" class="w-full md:w-auto order-2 md:order-1">
          <select id="buildings-location"
                  class="md:hidden w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  @change="setLocationIndex">
            <option :selected="!activeLocation" value="">{{$t('labels.all')}}</option>
            <option v-for="(location, index) in locations" :selected="location.name == activeLocation?.name" :value="index">{{$t('cities.' + location.name)}}</option>
          </select>
          <ul class="hidden md:flex flex-wrap gap-8 text-sm font-medium text-center text-gray-500 dark:text-gray-400">
            <li>
              <a href="#" @click.prevent="setLocation(null)" class="inline-block px-5 py-2.5 rounded-md" :class="!activeLocation ? 'text-white bg-primary-900' : 'hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white'">{{$t('labels.all')}}</a>
            </li>
            <li v-for="location in locations">
              <a href="#"  @click.prevent="setLocation(location)" class="inline-block px-5 py-2.5 rounded-md" :class="location.name == activeLocation?.name ? 'text-white bg-primary-900' : 'hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white'">{{$t('cities.' + location.name)}}</a>
            </li>
          </ul>
        </div>

        <div class="w-full md:w-auto order-1 md:order-2 md:ml-auto">
          <label for="buildings-search" class="sr-only">{{$t('placeholders.building_or_community')}}</label>
          <div class="relative md:min-w-[280px]">
            <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
              </svg>
            </div>
            <input
              id="buildings-search"
              type="search"
              :placeholder="$t('placeholders.building_or_community')"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ps-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              v-model="query"
              @input="onSearchInput"
            />
          </div>
        </div>
      </div>
      <!-- Skeleton loader -->
      <div v-if="loading"
           class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
        <CardPropertySkeleton v-for="n in hitsPerPage" :key="n"/>
      </div>
      <div v-else-if="hits && hits.length > 0"
           class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
        <CardProject v-for="hit in hits" :key="hit.id" :listing="hit"/>
        <template v-if="loadingMore">
          <CardPropertySkeleton v-for="n in 24" :key="'more-' + n"/>
        </template>
      </div>
      <div v-else class="text-gray-500 dark:text-gray-400">{{$t('messages.no_buildings')}}</div>
      <div v-if="hits && hits.length > 0 && !loading && totalHits > hitsPerPage" class="mt-10 flex justify-center">
        <button
           @click="showMore"
           :disabled="loadingMore"
           class="w-full md:w-auto md:px-20 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
          {{$t('buttons.more_buildings')}} ({{ moreBuildingsText }})
        </button>
      </div>
    </MicsContainer>
  </section>
</template>

<script setup lang="ts">
import { ref, toRaw, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {useWebsiteConfigurationStore} from '~/stores/websiteConfigurationStore';
const websiteConfigurationStore = useWebsiteConfigurationStore();
const { $helpers } = useNuxtApp();
const { t } = useI18n();
const {result, search} = useAlgoliaSearch('listings')
const hits = ref([]);
const totalHits = ref(0);
const loading = ref(true);
const loadingMore = ref(false);
const hitsPerPage = ref(24);

const locations = ref([]);
const allLocations = ref([]);
const activeLocation = ref(null);
const query = ref('');

const moreBuildingsText = computed(() => {
  const remaining = totalHits.value - hitsPerPage.value;
  if (remaining > 100) {
    return '100+';
  }
  return Math.max(0, remaining);
});

function locationCitySlugs(location) {
  // A region (e.g. pacific-coast-beaches) has no listings under its own slug;
  // its listings live under the child city slugs whose `region` points back to it.
  if (location.type === 'region') {
    return allLocations.value
      .filter(config => config.region === location.name)
      .map(config => config.name);
  }
  return [location.name];
}

function buildFilters() {
  let filters = `language_code:${websiteConfigurationStore.configuration.languageCode} AND listing_id:${websiteConfigurationStore.configuration.listingId} AND announce_type:project-announces`;
  if (activeLocation.value) {
    const slugs = locationCitySlugs(activeLocation.value);
    if (slugs.length) {
      filters += ` AND (${slugs.map(slug => `location.city.slug:${slug}`).join(' OR ')})`;
    }
  }
  return filters;
}

function setLocationIndex(event) {
  setLocation(locations.value[event.target.value] ?? null);
}

function setLocation(location) {
  activeLocation.value = location;
  hitsPerPage.value = 24;
  runSearch();
}

function onSearchInput() {
  hitsPerPage.value = 24;
  runSearch();
}

function showMore() {
  hitsPerPage.value += 24;
  runSearch(true);
}

async function runSearch(append = false) {
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
  }
  await search({
    query: query.value,
    requestOptions: {
      hitsPerPage: hitsPerPage.value,
      distinct: true,
      filters: buildFilters()
    },
  });
  totalHits.value = result.value.nbHits;
  hits.value = toRaw(result.value.hits);
  loading.value = false;
  loadingMore.value = false;
}

useHead({
  title: t('buildings_in') + t('countries.' + $helpers.getConfigurationKey('countryCode')),
});

onMounted(async () => {
  allLocations.value = $helpers.getConfigurationKey('locations');
  locations.value = allLocations.value.filter(location => location.primary);
  runSearch();
})
</script>
